import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react-dom';

import { cx } from '@libs/classnames';
import { ContextMenu, ContextMenuItem } from '@ui/components/ContextMenu/ContextMenu';
import { ArrowIcon } from '@ui/icons/ArrowIcon/ArrowIcon';

import styles from './Dropdown.module.css';

// icon — значок справа от подписи пункта (статус, отметка и т.п.)
// disabled — пункт виден, но недоступен (например, вариант фильтра без результатов)
export type DropdownOption<T = string> = {
  value: T;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
};

type Props<T = string> = {
  className?: string;
  value?: T;
  options: readonly DropdownOption<T>[];
  // Подпись, пока значение не выбрано: пункт-заглушка в options не нужен
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: T) => void;
};

export function Dropdown<T = string>({
  className,
  value,
  options,
  placeholder,
  disabled,
  onChange,
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const floating = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      size({
        apply: ({ rects, elements, availableHeight }) => {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.min(260, availableHeight)}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  // Клик вне триггера и вне (портального) меню — закрываем.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      const reference = floating.refs.reference.current as HTMLElement | null;
      const menu = floating.refs.floating.current;
      if (reference?.contains(target) || menu?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [isOpen, floating.refs]);

  const current = options.find((o) => o.value === value);

  const onSelect = (next: T) => {
    setIsOpen(false);
    if (next !== value) {
      onChange?.(next);
    }
  };

  return (
    <div className={cx(styles.dropdown, className)} ref={floating.refs.setReference}>
      <button
        type="button"
        className={styles.dropdown__button}
        disabled={disabled}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className={cx(styles.dropdown__label, !current && styles.is_placeholder)}>
          {current?.label ?? placeholder}
        </span>
        <span className={cx(styles.dropdown__arrow, isOpen && styles.is_open)}>
          <ArrowIcon />
        </span>
      </button>

      {isOpen &&
        !disabled &&
        createPortal(
          <ContextMenu
            ref={floating.refs.setFloating}
            className={styles.dropdown__menu}
            style={floating.floatingStyles}
            animated
          >
            {options.map((o) => (
              <ContextMenuItem
                key={o.label}
                icon={o.icon}
                disabled={o.disabled || o.value === value}
                onClick={() => onSelect(o.value)}
              >
                {o.label}
              </ContextMenuItem>
            ))}
          </ContextMenu>,
          document.body,
        )}
    </div>
  );
}
